import config from "./S360Config";
import HWLog from "../HWLibrary/HWLog";
import { s360ConfigService } from "./S360Config";
import HWUser from "../HWLibrary/HWUser";
import { isAnyEmpty } from "../HWLibrary/HWUtils.jsx";
import uuid from "react-uuid";
const resolve = require("@xmpp/resolve");
const { client, json, xml, jid } = require("@xmpp/client");
const debug = require("@xmpp/debug");

/* Singleton XMPP Service 
   - XML Tag Format: <Tag attributes > body </Tag>  ~  xml('tag', attributes, body)
*/
class XMPPService {
  constructor() {
    if (!XMPPService.instance) {
      XMPPService.instance = this;
    }
    return XMPPService.instance;
  }

  xmpp = null;
  toAddress = () => {
    return "server@" + s360ConfigService.config.host;
  };
  clientConfiguration = {};
  validResponseMessageCommands = [
    "GET_AGENTS_RESPONSE",
    "GET_SESSIONS_RESPONSE",
    "CREATE_SESSION",
    "BOT_REPLY",
    "AGENT_REPLY",
    "HEALTH_PING",
    "HISTORY_RESPONSE",
    "PING_RESPONSE",
    "SESSION_CLOSE",
    "CHAT_STATUS",
    "TYPING_INDICATOR",
    "SEND_CHAT_TRANSCRIPT_RESPONSE",
  ];

  isConfigured = false;
  connectionStatus = "notStarted";
  isOnline = false;

  onMessageCallback = null;
  configure = (onMessageCallback) => {
    this.onMessageCallback = onMessageCallback;
    let xmppUserName = HWUser.xmppUserName();
    let xmppUserPass = HWUser.xmppUserPassword();
    if (isAnyEmpty(xmppUserName, xmppUserPass)) {
      HWLog.info("Credentials Not Available: ");
      return Promise.reject("Credentials Not Available: ");
    }

    let serviceURLToConnect =
      "wss://" + s360ConfigService.config.host + ":5443/ws";
    this.clientConfiguration = {
      service: serviceURLToConnect,
      username: xmppUserName,
      password: xmppUserPass,
    };
    this.xmpp = client(this.clientConfiguration);

    this.watchForStateUpdates();
    this.watchForMessages(this.onMessageCallback);

    this.isConfigured = true;
    HWLog.info("XMPP Configured: ");
    return this.connect();
  };

  connect = () => {
    if (!this.xmpp) {
      Promise.reject("XMPP Client Not Configured.");
    }
    if (this.isOnline) {
      Promise.resolve("Already Connected: ");
    }
    return new Promise((resolve, reject) => {
      this.xmpp.on("online", (data) => {
        HWLog.info("XMPP ONLINE: ", data);
        resolve("ONLINE");
        this.isOnline = true;
      });
      this.xmpp.start();
    });
  };

  watchForStateUpdates = () => {
    if (!this.xmpp) return;
    HWLog.info("STARTED XMPP STATE LISTENERS");
    debug(this.xmpp, true);
    this.xmpp.on("connecting", () => {
      HWLog.info("Connecting...");
    });
    this.xmpp.on("connected", () => {
      HWLog.info("Connected");
    });
    this.xmpp.on("error", (err) => {
      HWLog.error(err, "Error On Socket Connection");
    });
    this.xmpp.on("offline", () => {
      HWLog.info("Offline");
    });
    this.xmpp.on("opening", () => {
      HWLog.info("Opening...");
    });
    this.xmpp.on("open", () => {
      HWLog.info("Open");
    });
    this.xmpp.on("closing", () => {
      HWLog.info("Closing");
    });
    this.xmpp.on("close", () => {
      HWLog.info("Close");
    });
    this.xmpp.on("disconnecting", () => {
      HWLog.info("Disconnecting");
    });
    this.xmpp.on("disconnect", () => {
      HWLog.info("Disconnect");
    });
  };

  fetchAgents = (limit) => {
    const configId = s360ConfigService.config.configId;
    const params = {
      configId: configId,
      command: "GET_AGENTS_REQUEST",
      convoType: "TEXT",
      fromUi: false,
      assignedTo: -1,
      isJabber: true,
      fromApi: false,
      firstMessageTimestamp: 0,
    };
    params["limit"] = limit || 5;
    params["sourceType"] = HWUser.sourceType() || 60;
    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    HWLog.info("FETCH AGENTS REQUEST: ", message);
    return this.xmpp.send(message);
  };

  fetchConversations = (limit, lastFetchedSession) => {
    const customerId = HWUser.customerId();
    const clientUser = HWUser.xmppUserName();
    const params = {
      configId: s360ConfigService.config.configId,
      command: "GET_SESSIONS_REQUEST",
      customerId,
      convoType: "TEXT",
      clientUser,
      fromUi: false,
      assignedTo: -1,
      isJabber: true,
      fromApi: false,
      firstMessageTimestamp: 0,
    };
    params["limit"] = limit || 30;
    params["sourceType"] = HWUser.sourceType() || 60;
    if (lastFetchedSession) {
      params["sessionInfo"] = lastFetchedSession;
    }
    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    HWLog.info("FETCH CONVERSATIONS REQUEST: ", message);
    return this.xmpp.send(message);
  };

  createSession = () => {
    const customerId = HWUser.customerId();
    const clientUser = HWUser.xmppUserName();
    const sourceType = HWUser.sourceType() || 60;
    const params = {
      configId: s360ConfigService.config.configId,
      command: "CREATE_SESSION",
      customerId,
      convoType: "TEXT",
      clientUser,
      fromUi: false,
      assignedTo: -1,
      sourceType,
      isJabber: true,
      fromApi: false,
      firstMessageTimestamp: 0,
    };
    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    HWLog.info("CREATE SESSION REQUEST:", message);
    return this.xmpp.send(message);
  };

  fetchAgentHealthPing = (sessionId) => {
    const customerId = HWUser.customerId();
    const clientUser = HWUser.xmppUserName();
    const params = {
      configId: s360ConfigService.config.configId,
      command: "HEALTH_PING",
      customerId,
      clientUser,
      fromUi: false,
      isJabber: true,
      fromApi: false,
    };
    params["sessionId"] = sessionId;
    params["sourceType"] = HWUser.sourceType() || 60;
    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    HWLog.info("FETCH HEALTH PING REQUEST:", message);
    return this.xmpp.send(message);
  };

  fetchConversationHistory = (sessionInfo, fromId, limit) => {
    const sourceType = HWUser.sourceType() || 60;
    const params = {
      configId: s360ConfigService.config.configId,
      command: "HISTORY_REQUEST",
      convoType: "TEXT",
      fromUi: false,
      assignedTo: -1,
      firstMessageTimestamp: 0,
      sessionInfo,
      sourceType,
      isJabber: true,
      fromApi: false,
    };
    if (fromId) {
      params["fromId"] = fromId;
    }
    if (limit) {
      params["limit"] = limit;
    }
    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    HWLog.info("CONVERSATION HISTORY REQUEST:", message);
    return this.xmpp.send(message);
  };

  sendUserMetaData = (userDetails, sessionId) => {
    console.log("sent", userDetails, sessionId);

    const params = {
      sessionId: sessionId,
      command: "SAVE_USER_DETAILS",
      userDetails: userDetails,
      isJabber: true,
      fromApi: false,
    };

    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    console.log("ff", message);
    HWLog.info("SEND USER META DATA:", message);
    return this.xmpp.send(message);
  };

  sendMessage = (sessionId, messageData, convoType) => {
    const customerId = HWUser.customerId();
    const clientUser = HWUser.xmppUserName();
    const sourceType = HWUser.sourceType() || 60;
    const sourceId = HWUser.sourceId();
    const customerFirstName = HWUser.customerFirstName();
    const customerLastName = HWUser.customerLastName();
    const appId = HWUser.appId();
    const params = {
      configId: s360ConfigService.config.configId,
      sourceId: sourceId,
      sessionId: sessionId,
      command: "CUSTOMER_REPLY",
      customerId,
      message: messageData,
      convoType: convoType,
      customerFirstName: customerFirstName,
      customerLastName: customerLastName,
      appId: appId,
      clientUser,
      fromUi: false,
      assignedTo: -1,
      sourceType,
      isJabber: true,
      fromApi: false,
      creator: "CUSTOMER",
      firstMessageTimestamp: 0,
    };
    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    console.log("SEND MESSAGE REQUEST:", message);
    return this.xmpp.send(message);
  };

  sendGif = (sessionId, messageData, convoType) => {
    console.log(sessionId, "hello");
    const customerId = HWUser.customerId();
    const clientUser = HWUser.xmppUserName();
    const sourceType = HWUser.sourceType() || 60;
    const sourceId = HWUser.sourceId();
    const customerFirstName = HWUser.customerFirstName();
    const customerLastName = HWUser.customerLastName();
    const pageName = HWUser.pageName();
    const appId = HWUser.appId();

    const params = {
      msgId: "gCkzCX8CkfCh3rtoK4nm-Uc3",
      configId: s360ConfigService.config.configId,
      sourceId: sourceId,
      sessionId: sessionId,
      command: "CUSTOMER_REPLY",
      customerId: customerId,
      message: messageData,
      convoType: convoType,
      clientUser: clientUser,
      customerFirstName: customerFirstName,
      typing: false,
      appId: appId,
      fromUi: false,
      assignedTo: -4,
      pageName: pageName,
      firstMessageTimestamp: 0,
      sourceType: sourceType,
      isJabber: true,
      conversationSaved: true,
      fromApi: false,
    };

    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    HWLog.info("SEND MESSAGE REQUEST:", message);
    return this.xmpp.send(message);
  };

  // MARK: INPUT: Session Id, Typing Status: true or false.
  sendTypingIndicatorStatus = (sessionId, typingStatus) => {
    const customerId = HWUser.customerId();
    const sourceType = HWUser.sourceType() || 60;
    const params = {
      configId: s360ConfigService.config.configId,
      sessionId: sessionId,
      command: "TYPING_INDICATOR",
      customerId,
      typer: "CUSTOMER",
      typing: typingStatus,
      fromUi: false,
      sourceType,
      isJabber: true,
      fromApi: false,
    };
    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    HWLog.info("SEND TYPING INDICATOR STATUS REQUEST:", message);
    return this.xmpp.send(message);
  };

  closeSession = (sessionId) => {
    const customerId = HWUser.customerId();
    const clientUser = HWUser.xmppUserName();
    const sourceType = HWUser.sourceType() || 60;
    const params = {
      configId: s360ConfigService.config.configId,
      sessionId: sessionId,
      command: "SESSION_CLOSE_REQUEST",
      customerId,
      convoType: "TEXT",
      clientUser,
      fromUi: false,
      assignedTo: -1,
      sourceType,
      isJabber: true,
      fromApi: false,
      firstMessageTimestamp: 0,

      uuId: uuid(),
    };
    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    HWLog.info("SEND CLOSE SESSION REQUEST:", message);
    return this.xmpp.send(message);
  };

  sendChatTranscript = (sessionInfo, emailId) => {
    const customerId = HWUser.customerId();
    const clientUser = HWUser.xmppUserName();
    const sourceType = HWUser.sourceType() || 60;
    const params = {
      configId: s360ConfigService.config.configId,
      sessionInfo: sessionInfo,
      emailId,
      command: "SEND_CHAT_TRANSCRIPT_REQUEST",
      customerId,
      convoType: "TEXT",
      clientUser,
      fromUi: false,
      assignedTo: -1,
      sourceType,
      isJabber: true,
      fromApi: false,
      firstMessageTimestamp: 0,
    };
    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    HWLog.info("SEND CHAT TRANSCRIPT REQUEST:", message);
    return this.xmpp.send(message);
  };

  sendUserLocation = (lat, long, sessionId) => {
    const customerId = HWUser.customerId();
    const clientUser = HWUser.xmppUserName();
    const sourceType = HWUser.sourceType() || 60;
    const customerFirstName = HWUser.customerFirstName();
    const appId = HWUser.appId();
    const pageName = HWUser.pageName();
    const currentTimeStamp = new Date().getTime();
    const messageData = {
      text: lat + "," + long,
      sentTimestamp: currentTimeStamp,
    };
    const params = {
      configId: s360ConfigService.config.configId,
      sessionId: sessionId,
      command: "CUSTOMER_REPLY",
      customerId,
      message: messageData,
      convoType: "LOCATION",
      customerFirstName: customerFirstName,
      appId: appId,
      clientUser,
      pageName: pageName,
      fromUi: false,
      assignedTo: -1,
      sourceType,
      isJabber: true,
      fromApi: false,
      firstMessageTimestamp: currentTimeStamp,
    };
    const attributes = { type: "chat", to: this.toAddress() };
    const content = xml("body", {}, JSON.stringify(params));
    const message = xml("message", attributes, content);
    HWLog.info("SEND USER LOCATION REQUEST:", message);
    return this.xmpp.send(message);
  };

  watchForMessages = (onMessageCallback) => {
    if (!this.xmpp) return;
    HWLog.info("WATCHING FOR XMPP MESSAGES");
    this.xmpp.on("stanza", async (stanza) => {
      HWLog.info("Stanza", stanza);
      if (onMessageCallback) {
        let message = this.parseXMPPStanza(stanza);
        onMessageCallback(message);
      }
      // if (stanza.is("createSession")) {
      //   await this.sendMessage(stanza.body);
      //   await this.xmpp.stop();
      // }
    });
  };

  // Refer to MESSAGE STANZA SAMPLE at end of this file.
  parseXMPPStanza = (stanza) => {
    try {
      let jsonStanza = stanza;
      if (
        !jsonStanza ||
        !jsonStanza.name ||
        jsonStanza.name !== "message" ||
        !jsonStanza.attrs ||
        !jsonStanza.attrs.to ||
        !this.clientConfiguration ||
        !this.clientConfiguration.username ||
        !jsonStanza.attrs.to.startsWith(this.clientConfiguration.username) ||
        !jsonStanza.children ||
        jsonStanza.children.length === 0
      ) {
        HWLog.info("Not A Valid Stanza: ");
        return null;
      }
      let xmlMessageBody = jsonStanza.children.filter(
        (xmlTag) => xmlTag.name === "body"
      )[0];
      if (
        !xmlMessageBody ||
        !xmlMessageBody.children ||
        xmlMessageBody.children.length !== 1
      ) {
        HWLog.info("No Body In Stanza: ");
        return null;
      }
      let parsedMessageBody = JSON.parse(xmlMessageBody.children[0]);
      return this.validateBodyJSON(parsedMessageBody)
        ? parsedMessageBody
        : null;
    } catch (error) {
      return HWLog.warn("XMPP STANZA PARSE ERROR:" + error);
    }
  };

  validateBodyJSON = (xmppBodyMessage) => {
    if (
      !xmppBodyMessage ||
      !xmppBodyMessage.command ||
      this.validResponseMessageCommands.indexOf(xmppBodyMessage.command) === -1
    ) {
      HWLog.info("Not A Valid Body Message JSON: ");
      console.log("ZZZZZZZ");
      console.log(JSON.stringify(xmppBodyMessage));
      return false;
    }
    return true;
  };
}

var xmppService = new XMPPService();
export default xmppService;

/*
  MESSAGE STANZA SAMPLE:
  {
  "name": "message",
  "attrs": {
    "xmlns": "jabber:client",
    "xml:lang": "en",
    "to": "user_178@devdata.simplify360.com/1193994283354509787321410",
    "from": "server@devdata.simplify360.com/10775965103696341105570",
    "type": "chat",
    "id": "8MmIv-22094"
  },
  "children": [
    {
      "name": "body",
      "attrs": {},
      "children": [
        "{\"command\":\"GET_AGENTS_RESPONSE\",\"clientUser\":\"user_178\",\"typing\":false,\"fromUi\":false,\"listAgentInfo\":[],\"sourceType\":0,\"isJabber\":false,\"fromApi\":false}"
      ]
    }
  ]
}

 switch(xmppBodyMessage.command) {
      case "GET_AGENTS_RESPONSE": 
      case "GET_SESSIONS_RESPONSE": 
      case "CREATE_SESSION": 
      case "BOT_REPLY": 
  }
*/
